/**
 * Alpine.js 기반 Year Picker — 년도만 선택하는 작은 팝오버
 *
 * 사용 맥락: monthly 일정 페이지처럼 월(1~12) UI 가 이미 있고
 * 년도만 빠르게 바꾸고 싶을 때.
 *
 * 사용법:
 *   <div x-data="yearPicker({ defaultYear: 2026, onConfirm: (y) => {...} })"></div>
 *
 * 옵션:
 *   - defaultYear: 초기 선택 년도 (기본: 현재 년)
 *   - autoOpen: 초기화 시 자동 열기 (기본: false)
 *   - confirmText: 확정 버튼 텍스트 (기본: '이동')
 *   - cancelText: 취소 버튼 텍스트 (기본: '취소')
 *   - onConfirm: (year) => {} 확정 콜백
 *   - onCancel: () => {} 취소 콜백
 *
 * 디자인:
 *   - 4×3 그리드 (12년 한 화면)
 *   - 헤더 [<] 시작년 - 끝년 [>] (12년 단위 이동)
 *   - 선택된 년: 검정 배경, 오늘 년: 회색 배경
 */
export default (options = {}) => ({
    open: false,
    year: 0,
    tempYear: 0,
    decadeStart: 0,

    options: {
        defaultYear: null,
        autoOpen: false,
        confirmText: '이동',
        cancelText: '취소',
        onConfirm: null,
        onCancel: null,
        ...options
    },

    pickerEl: null,

    init() {
        const initial = this.options.defaultYear || new Date().getFullYear();
        this.year = initial;
        this.tempYear = initial;
        this.decadeStart = Math.floor(initial / 12) * 12;

        this._createUI();

        // ESC 키로 닫기
        this.$el.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.open) {
                this.cancel();
            }
        });

        // 외부 클릭 시 닫기
        document.addEventListener('click', (e) => {
            if (this.open && !this.$el.contains(e.target)) {
                this.cancel();
            }
        });

        if (this.options.autoOpen) {
            this.$nextTick(() => { this.open = true; });
        }
    },

    _createUI() {
        this.pickerEl = document.createElement('div');
        this.pickerEl.className = 'absolute top-0 left-0 z-50 p-4 mt-2 antialiased bg-white border rounded-lg shadow w-[17rem]';
        this.pickerEl.style.borderColor = 'var(--ws-line-2)';
        this.pickerEl.style.display = 'none';

        // 내부 클릭 버블링 차단 (외부 클릭 핸들러 오작동 방지)
        this.pickerEl.addEventListener('click', e => e.stopPropagation());

        this.$el.style.position = 'relative';
        this.$el.appendChild(this.pickerEl);

        this.$watch('open', (isOpen) => {
            this.pickerEl.style.display = isOpen ? 'block' : 'none';
            if (isOpen) this._render();
        });
    },

    _render() {
        const start = this.decadeStart;
        const end = start + 11;
        const today = new Date().getFullYear();

        let html = `
            <div class="flex justify-between items-center mb-3">
                <button type="button" data-action="prev"
                        class="inline-flex p-1 rounded-full transition cursor-pointer hover:bg-gray-100">
                    <svg class="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span class="text-sm font-semibold text-gray-700">${start} - ${end}</span>
                <button type="button" data-action="next"
                        class="inline-flex p-1 rounded-full transition cursor-pointer hover:bg-gray-100">
                    <svg class="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
            <div class="grid grid-cols-3 gap-1.5">
        `;

        for (let y = start; y <= end; y++) {
            const isSelected = y === this.tempYear;
            const isToday = y === today;

            let cls = 'flex items-center justify-center h-10 text-sm rounded-full cursor-pointer transition';
            if (isSelected) {
                cls += ' bg-neutral-800 text-white hover:bg-neutral-700';
            } else if (isToday) {
                cls += ' bg-neutral-100 text-gray-900 font-semibold';
            } else {
                cls += ' text-gray-700 hover:bg-neutral-100';
            }

            html += `<button type="button" class="${cls}" data-year="${y}">${y}</button>`;
        }

        html += `
            </div>
            <div class="flex justify-end gap-2 pt-3 mt-3 border-t border-neutral-200">
                <button type="button" data-action="confirm"
                        class="px-3 py-1.5 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500">
                    ${this.options.confirmText}
                </button>
                <button type="button" data-action="cancel"
                        class="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300">
                    ${this.options.cancelText}
                </button>
            </div>
        `;

        this.pickerEl.innerHTML = html;

        // 이벤트 바인딩
        this.pickerEl.querySelectorAll('[data-year]').forEach(btn => {
            btn.addEventListener('click', () => {
                this.tempYear = parseInt(btn.dataset.year);
                this._render();
            });
        });
        this.pickerEl.querySelector('[data-action="prev"]').addEventListener('click', () => {
            this.decadeStart -= 12;
            this._render();
        });
        this.pickerEl.querySelector('[data-action="next"]').addEventListener('click', () => {
            this.decadeStart += 12;
            this._render();
        });
        this.pickerEl.querySelector('[data-action="confirm"]').addEventListener('click', () => {
            this.confirm();
        });
        this.pickerEl.querySelector('[data-action="cancel"]').addEventListener('click', () => {
            this.cancel();
        });
    },

    toggle() { this.open = !this.open; },

    confirm() {
        this.year = this.tempYear;
        this.open = false;
        if (this.options.onConfirm) {
            this.options.onConfirm(this.year);
        }
    },

    cancel() {
        // 임시 선택을 확정값으로 되돌림 + decade view 도 리셋
        this.tempYear = this.year;
        this.decadeStart = Math.floor(this.year / 12) * 12;
        this.open = false;
        if (this.options.onCancel) {
            this.options.onCancel();
        }
    }
});
